// Single thin wrapper around @telegram-apps/sdk. Gives the rest of the app
// one well-known entry point for: (a) reading initDataRaw on startup,
// (b) signalling miniApp.ready() so Telegram un-blanks the WebView,
// (c) expanding the viewport so the Mini App fills the available height,
// (d) mounting the theme / viewport scopes so Telegram CSS variables and
//     the `.dark` class stay in sync with the user's Telegram theme +
//     keyboard / drawer-driven viewport changes.
//
// Every helper is wrapped in safeCall — outside Telegram (e.g. unit tests
// running under jsdom, dev preview in a normal browser tab) the SDK throws
// because there's no Telegram bridge, and we want callers to keep working.

import {
  bindThemeParamsCssVars,
  bindViewportCssVars,
  expandViewport,
  isThemeParamsDark,
  miniAppReady,
  mountThemeParams,
  mountViewport,
  retrieveLaunchParams,
} from "@telegram-apps/sdk";

import { applyColorScheme, resolveColorScheme } from "./theme";

export interface TelegramInitData {
  initDataRaw: string;
  platform: string;
  version: string;
}

export function readLaunchParams(): TelegramInitData | null {
  try {
    const params = retrieveLaunchParams();
    const initDataRaw = params.initDataRaw ?? "";
    if (!initDataRaw) return null;
    return {
      initDataRaw,
      platform: params.platform ?? "unknown",
      version: params.version ?? "unknown",
    };
  } catch {
    return null;
  }
}

/**
 * Tell Telegram the Mini App is ready to be shown. No-op outside the Telegram
 * bridge (dev preview in a regular browser, jsdom tests).
 */
export function signalMiniAppReady(): void {
  try {
    miniAppReady();
  } catch {
    /* not running inside Telegram — ignore */
  }
}

/**
 * Expand the Mini App viewport to the full available height. Same no-op
 * fallback as signalMiniAppReady().
 */
export function expandMiniAppViewport(): void {
  try {
    expandViewport();
  } catch {
    /* not running inside Telegram — ignore */
  }
}

/**
 * Wire Telegram's theme + viewport scopes into the document so:
 *   - Tailwind's `.dark` class on <html> matches Telegram's color scheme.
 *   - `--tg-viewport-height` / `--tg-viewport-stable-height` follow the
 *     keyboard + drawer animations (Mini Apps shrink when the OS keyboard
 *     opens — layouts pin to `var(--tg-viewport-stable-height)` to stay
 *     usable).
 *   - `--tg-theme-*` CSS variables are populated for any component that
 *     wants the user's Telegram chrome colors.
 *
 * Outside Telegram the helper installs a `prefers-color-scheme` listener
 * so dev preview in a normal browser still flips light/dark with the OS.
 *
 * Returns a cleanup function that tears down every subscription. Safe to
 * call once at app boot — re-mounts during React Strict Mode are guarded
 * by the SDK itself ("already mounted" is treated as a no-op here).
 */
export function mountTelegramChrome(
  root: HTMLElement = document.documentElement,
): () => void {
  const teardown: Array<() => void> = [];

  const tgMounted = tryMountTelegramScopes();

  // Apply the dark class up-front so the first paint matches the resolved
  // scheme (no light → dark flicker).
  const applyFromCurrentSignals = () => {
    const tgIsDark = tgMounted ? safeReadIsDark() : null;
    const prefersDark = readPrefersDark();
    applyColorScheme(root, resolveColorScheme({ tgIsDark, prefersDark }));
  };
  applyFromCurrentSignals();

  if (tgMounted) {
    // The SDK exposes signals from @telegram-apps/signals that have a `sub`
    // method returning an unsubscribe handle.
    try {
      const unsub = isThemeParamsDark.sub(applyFromCurrentSignals);
      teardown.push(unsub);
    } catch {
      /* signal subscription unavailable — fall through to media query */
    }
  }

  // Always also subscribe to prefers-color-scheme so the fallback path
  // (and dev preview) remain reactive. When Telegram has an opinion the
  // resolver ignores `prefersDark`, so this listener is harmless inside
  // Telegram and useful outside it.
  const mq = safeMatchMedia("(prefers-color-scheme: dark)");
  if (mq) {
    const handler = () => applyFromCurrentSignals();
    mq.addEventListener("change", handler);
    teardown.push(() => mq.removeEventListener("change", handler));
  }

  return () => {
    for (const fn of teardown) {
      try {
        fn();
      } catch {
        /* swallow — teardown best-effort */
      }
    }
  };
}

function tryMountTelegramScopes(): boolean {
  let any = false;
  try {
    mountThemeParams();
    any = true;
  } catch {
    /* not in Telegram or already mounted */
  }
  try {
    bindThemeParamsCssVars();
  } catch {
    /* already bound or not available */
  }
  try {
    mountViewport();
    any = true;
  } catch {
    /* not in Telegram or already mounted */
  }
  try {
    bindViewportCssVars();
  } catch {
    /* already bound or not available */
  }
  return any;
}

function safeReadIsDark(): boolean | null {
  try {
    return isThemeParamsDark();
  } catch {
    return null;
  }
}

function readPrefersDark(): boolean {
  const mq = safeMatchMedia("(prefers-color-scheme: dark)");
  return mq?.matches ?? false;
}

function safeMatchMedia(query: string): MediaQueryList | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }
  try {
    return window.matchMedia(query);
  } catch {
    return null;
  }
}
